import axios from 'axios';
import config from '../config';

// Create axios instance
const api = axios.create({
    baseURL: `${config.API_BASE_URL}${config.ADMIN_API_PREFIX}/${config.ADMIN_API_VERSION}`,
    timeout: config.TIMEOUTS.API_REQUEST,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: true // 🔑 ensures cookies (refreshToken) are sent automatically
});

const apiNoAuthHeader = axios.create({
    baseURL: `${config.API_BASE_URL}${config.ADMIN_API_PREFIX}/${config.ADMIN_API_VERSION}`,
    timeout: config.TIMEOUTS.API_REQUEST,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
});

// Request interceptor: add access token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor: handle 401 -> refresh token via cookie
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {

                // 🔑 Call refresh-token without passing refreshToken manually
                const { data } = await api.post(config.AUTH_ENDPOINTS.REFRESH_TOKEN);

                const accessToken = data.accessToken;

                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('tokenTimestamp', Date.now().toString());

                originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                authService.forceLogout();
                window.location.href = '/login?session=expired';
            }
        }
        return Promise.reject(error.response?.data || error.message);
    }
);

const authService = {

    storeAuthData: (authData) => {
        if (!authData) return;

        // Safely extract accessToken and user data
        const accessToken = authData.accessToken;

        const userData = {
            email: authData.email,
            phone: authData.phone,
            username: authData.username,
            name: authData.name,
            ipAddress: authData.ipAddress
        };

        try {
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('email', authData.email);
            localStorage.setItem('phone', authData.phone);
            localStorage.setItem('username', authData.username);
            localStorage.setItem('name', authData.name);
            localStorage.setItem('tokenTimestamp', Date.now().toString());

        } catch (error) {
            console.error('Error storing auth data:', error);
        }

    },

    getLoggedInUserName: () => {
        const user = localStorage.getItem("name");
        return !user?'Guest':user;
    },

    clearAuthData: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('email');
        localStorage.removeItem('phone');
        localStorage.removeItem('username');
        localStorage.removeItem('name');
        localStorage.removeItem('tokenTimestamp');
    },

    getUserData: () => {

        const user = localStorage.getItem("username");

        return user;

    },

    getAccessToken: () => localStorage.getItem('accessToken'),

    isAuthenticated: () => {
        return !!authService.getAccessToken() && !authService.isTokenExpired();
    },

    isTokenExpired: () => {
        const token = authService.getAccessToken();
        if (!token) return true;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Date.now() / 1000;
            return payload.exp < now;
        } catch {
            return true;
        }
    },

    verifyAccessToken: async () => {

        try {

            const accessToken = authService.getAccessToken();

            const {data} = await api.post(config.AUTH_ENDPOINTS.VERIFY_ACCESS_TOKEN,
                {accessToken},
                {withCredentials: true}
            );

            const valid = data.success;

            return valid;

        } catch (err) {
            authService.forceLogout();
            return null;
        }

    },

    getValidAccessToken: async () => {

        if (!authService.isTokenExpired()) {
            return authService.getAccessToken();
        }

        try {

            const { data } = await api.post(config.AUTH_ENDPOINTS.REFRESH_TOKEN);

            console.log('valid access token>>>>>>>>>>>>'+data);

            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('tokenTimestamp', Date.now().toString());

            return data.accessToken;

        } catch (err) {
            authService.forceLogout();
            return null;
        }

    },
    requestAccess: async (requestData) => {
        try {
            const response = await api.post(config.SIGNUP_ENDPOINTS.REQUEST_ACCESS, requestData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    login: async (credentials) => {

        try {

            const response = await apiNoAuthHeader.post(config.AUTH_ENDPOINTS.LOGIN, credentials); // 👈 no interceptors
            const resp = response.data;
            const userData = resp.data;
            const accessToken = userData.accessToken;

            if (accessToken) {
                authService.storeAuthData(userData);
            }

            return resp;

        } catch (error) {
            throw error.response?.data || error.message;
        }

    },

    logout: async () => {
        console.log('Logout initiated');
        try {
            const token = authService.getAccessToken();
            if (token) {
                await api.post(config.AUTH_ENDPOINTS.LOGOUT, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            authService.forceLogout();
        }
    },

    forceLogout: () => {
        authService.clearAuthData();
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
    },

    forgotPassword: async (emailData) => {
        try {
            const response = await apiNoAuthHeader.post(
                config.AUTH_ENDPOINTS.INITIATE_RESET_PASSWORD,
                emailData
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    resetPassword: async (resetData) => {
        try {
            const response = await apiNoAuthHeader.post(
                config.AUTH_ENDPOINTS.RESET_PASSWORD,
                resetData
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

};

export default authService;
